import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { toast } from '@/components/ui/use-toast'
import { realtimeService } from '@/services/realtimeService'

// Enhanced error handling for queries
const queryCache = new QueryCache({
  onError: (error: any, query) => {
    // Don't show error toasts for background refetches
    if (query.state.fetchStatus === 'fetching' && query.state.data !== undefined) {
      return
    }

    // Handle network errors gracefully
    if (error?.code === 'NETWORK_ERROR' || !navigator.onLine) {
      toast({
        title: 'Connection Issue',
        description: 'Unable to fetch latest data. Showing cached version.',
        variant: 'destructive'
      })
      return
    }

    // Handle other errors
    toast({
      title: 'Error',
      description: error?.message || 'Something went wrong',
      variant: 'destructive'
    })
  }
})

// Enhanced error handling for mutations
const mutationCache = new MutationCache({
  onError: (error: any, variables, context, mutation) => {
    // Handle offline mutations
    if (!navigator.onLine) {
      toast({
        title: 'Offline',
        description: 'Changes saved locally and will sync when online',
      })
      return
    }

    // Handle conflict errors
    if (error?.status === 409) {
      toast({
        title: 'Conflict Detected',
        description: 'Data has been modified by another user. Please resolve the conflict.',
        variant: 'destructive'
      })
      return
    }

    // Handle validation errors
    if (error?.status === 400) {
      toast({
        title: 'Validation Error',
        description: error?.message || 'Please check your input and try again',
        variant: 'destructive'
      })
      return
    }

    // Handle authorization errors
    if (error?.status === 401 || error?.status === 403) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to perform this action',
        variant: 'destructive'
      })
      return
    }

    // Generic error
    toast({
      title: 'Error',
      description: error?.message || 'Failed to save changes',
      variant: 'destructive'
    })
  },
  
  onSuccess: (data, variables, context, mutation) => {
    // Show success toast for important mutations
    const mutationKey = mutation.options.mutationKey
    if (mutationKey && shouldShowSuccessToast(mutationKey)) {
      toast({
        title: 'Success',
        description: getSuccessMessage(mutationKey),
      })
    }
  }
})

// Create enhanced query client
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Retry configuration
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false
        }
        
        // Don't retry when offline
        if (!navigator.onLine) {
          return false
        }
        
        // Retry up to 3 times for other errors
        return failureCount < 3
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Stale time - how long data is considered fresh
      staleTime: 5 * 60 * 1000, // 5 minutes
      
      // Cache time - how long data stays in cache
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      
      // Refetch on window focus (when user returns to tab)
      refetchOnWindowFocus: true,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
      
      // Refetch on mount if data is stale
      refetchOnMount: true,
      
      // Network mode - how to handle offline scenarios
      networkMode: 'offlineFirst', // Try cache first when offline
    },
    
    mutations: {
      // Retry configuration for mutations
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false
        }
        
        // Don't retry when offline (will be queued instead)
        if (!navigator.onLine) {
          return false
        }
        
        // Retry up to 2 times for server errors
        return failureCount < 2
      },
      
      // Network mode for mutations
      networkMode: 'online', // Only execute when online
    }
  }
})

// Utility functions
function shouldShowSuccessToast(mutationKey: any[]): boolean {
  const importantMutations = [
    'createStudent',
    'updateStudent',
    'deleteStudent',
    'createRoutine',
    'updateRoutine',
    'deleteRoutine',
    'createStudyBlock',
    'updateStudyBlock',
    'deleteStudyBlock'
  ]
  
  return mutationKey.some(key => importantMutations.includes(key))
}

function getSuccessMessage(mutationKey: any[]): string {
  const key = mutationKey.find(k => typeof k === 'string')
  
  switch (key) {
    case 'createStudent':
      return 'Student created successfully'
    case 'updateStudent':
      return 'Student updated successfully'
    case 'deleteStudent':
      return 'Student deleted successfully'
    case 'createRoutine':
      return 'Routine created successfully'
    case 'updateRoutine':
      return 'Routine updated successfully'
    case 'deleteRoutine':
      return 'Routine deleted successfully'
    case 'createStudyBlock':
      return 'Study block created successfully'
    case 'updateStudyBlock':
      return 'Study block updated successfully'
    case 'deleteStudyBlock':
      return 'Study block deleted successfully'
    default:
      return 'Changes saved successfully'
  }
}

// Enhanced query client with offline support
export function createOfflineQueryClient(): QueryClient {
  const client = new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        // More aggressive caching for offline support
        staleTime: navigator.onLine ? 5 * 60 * 1000 : 30 * 60 * 1000,
        gcTime: navigator.onLine ? 10 * 60 * 1000 : 60 * 60 * 1000,
        
        // Retry logic that considers network status
        retry: (failureCount, error: any) => {
          if (!navigator.onLine) return false
          if (error?.status >= 400 && error?.status < 500) return false
          return failureCount < 3
        },
        
        // Network mode for better offline handling
        networkMode: 'offlineFirst',
      },
      
      mutations: {
        // Queue mutations when offline
        networkMode: 'offlineFirst',
        
        retry: (failureCount, error: any) => {
          if (!navigator.onLine) return false
          if (error?.status >= 400 && error?.status < 500) return false
          return failureCount < 2
        }
      }
    }
  })

  // Listen for online/offline events
  window.addEventListener('online', () => {
    // Invalidate all queries when coming back online
    client.invalidateQueries()
    
    // Resume paused mutations
    client.resumePausedMutations()
  })

  window.addEventListener('offline', () => {
    // Update query client options for offline mode
    client.setDefaultOptions({
      queries: {
        staleTime: 30 * 60 * 1000, // 30 minutes
        gcTime: 60 * 60 * 1000, // 1 hour
        networkMode: 'offlineFirst',
      }
    })
  })

  return client
}

// Optimistic update helpers
export function createOptimisticUpdate<T>(
  queryKey: string[],
  updateFn: (oldData: T | undefined, newData: any) => T
) {
  return {
    onMutate: async (newData: any) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey })
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData<T>(queryKey)
      
      // Optimistically update
      queryClient.setQueryData<T>(queryKey, (old) => updateFn(old, newData))
      
      // Return context with previous data
      return { previousData }
    },
    
    onError: (error: any, newData: any, context: any) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
    },
    
    onSettled: () => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey })
    }
  }
}

// Batch update helper
export function batchUpdates(updates: Array<() => void>) {
  queryClient.getQueryCache().batch(() => {
    updates.forEach(update => update())
  })
}

// Prefetch helper with error handling
export async function safePrefetch(
  queryKey: string[],
  queryFn: () => Promise<any>,
  options: any = {}
) {
  try {
    await queryClient.prefetchQuery({
      queryKey,
      queryFn,
      staleTime: 5 * 60 * 1000,
      ...options
    })
  } catch (error) {
    console.warn('Prefetch failed:', error)
    // Don't throw - prefetch failures shouldn't break the app
  }
}

// Cache warming for critical data
export function warmCache() {
  // Prefetch user profile
  safePrefetch(['profile'], () => 
    fetch('/api/v2/profiles/me').then(res => res.json())
  )
  
  // Prefetch notifications
  safePrefetch(['notifications'], () =>
    fetch('/api/v2/notifications?limit=10').then(res => res.json())
  )
}

// Cache cleanup for memory management
export function cleanupCache() {
  // Remove old queries
  queryClient.getQueryCache().clear()
  
  // Clear mutation cache
  queryClient.getMutationCache().clear()
}

// Export configured client as default
export default queryClient